/**
 * Badges semánticos compartidos entre el viewer (lista) y el detalle de
 * artículos. Centraliza el mapeo clasificación → color para que el QA escanee
 * a ojo y para no duplicar la lógica de color entre pantallas.
 *
 * Convención de color (clases `.badge` de tokens.css):
 *   - sensitivity: safe → ok (verde), mild → neutral, sensitive → warn,
 *     graphic → error. El coach evita abrir con lo sensible/gráfico.
 *   - status: active → ok, hidden → neutral (oculto del coach), error → error.
 *   - cefr: badge informativo neutro (el nivel no es "bueno/malo").
 */
import type { ReactElement } from 'react';

type BadgeTone = 'b-ok' | 'b-warn' | 'b-error' | 'b-info' | 'b-neutral';

function Badge({ tone, children, title }: {
  tone: BadgeTone;
  children: string;
  title?: string;
}): ReactElement {
  return (
    <span className={`badge ${tone}`} title={title}>
      {children}
    </span>
  );
}

const SENSITIVITY_TONE: Record<string, BadgeTone> = {
  safe: 'b-ok',
  mild: 'b-neutral',
  sensitive: 'b-warn',
  graphic: 'b-error',
};

export function SensitivityBadge({ value }: { value: string | null }): ReactElement {
  if (!value) return <span className="muted">—</span>;
  const tone = SENSITIVITY_TONE[value] ?? 'b-neutral';
  return <Badge tone={tone} title="Sensibilidad (clasificación LLM)">{value}</Badge>;
}

const STATUS_TONE: Record<string, BadgeTone> = {
  active: 'b-ok',
  hidden: 'b-neutral',
  error: 'b-error',
};

export function StatusBadge({ value }: { value: string | null }): ReactElement {
  if (!value) return <span className="muted">—</span>;
  const tone = STATUS_TONE[value] ?? 'b-neutral';
  return <Badge tone={tone} title="Estado del artículo">{value}</Badge>;
}

export function CefrBadge({ value }: { value: string | null }): ReactElement {
  if (!value) return <span className="muted">—</span>;
  return <Badge tone="b-info" title="Nivel CEFR para leer/conversar el tema">{value}</Badge>;
}
